import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { Table } from 'legacy/lut/library/list'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { TplFunction } from 'core-components/library/list/types'

import { TMarketYoyGrowthExpectationRtm } from '../../api/types'
import { MARKET_YOY_GROWTH_EXPECTATION_TRANSLATION_KEYS } from '../../configs/translations'
import { IMarketYoyGrowthEditTarget } from '../../lib'
import {
  growthEditsDiscarded,
  growthValueEdited,
  offeringExpandToggled,
  requestApplyYoyGrowthPlanningChanges,
  requestApplyYoyGrowthPlanningDetailsChanges,
  selectHasMarketYoyGrowthDirtyEdits,
  selectIsMarketYoyGrowthPreviousFYearSelected,
  selectIsMarketYoyGrowthSaving,
  selectMarketYoyGrowthExpectationCategories,
  selectMarketYoyGrowthExpectationExpandedOfferings,
  selectMarketYoyGrowthExpectationGeoMarkets,
  selectMarketYoyGrowthExpectationsWithEdits,
  selectMarketYoyGrowthExpectationsWorkflowState,
  selectMarketYoyGrowthExpectationUserInfo,
  selectMarketYoyGrowthSaveError,
  selectMarketYoyGrowthSelectedFYear,
} from '../../model'
import { MarketYoyGrowthExpectationBanner } from '../marketYoyGrowthExpectationBanner'

import { ExportButton } from './components/exportButton/ExportButton'
import { FYearSelect } from './components/fYearSelect/FYearSelect'
import { RtmSegmentedControl } from './components/rtmSegmentedControl/RtmSegmentedControl'
import { getColumns } from './helpers/getColumns'
import { getMarketYoyGrowthExpectationGroups } from './helpers/getMarketYoyGrowthExpectationGroups'
import { TMarketYoyGrowthExpectationTableRow } from './types'

import styles from './MarketYoyGrowthExpectationTable.module.scss'

const KEYS = MARKET_YOY_GROWTH_EXPECTATION_TRANSLATION_KEYS

interface IMarketYoyGrowthExpectationTableProps {
  isEditable: boolean
}

export const MarketYoyGrowthExpectationTable = ({
  isEditable,
}: IMarketYoyGrowthExpectationTableProps) => {
  const dispatch = useAppDispatch()

  const userInfo = useSelector(selectMarketYoyGrowthExpectationUserInfo)
  const planningDetails = useSelector(selectMarketYoyGrowthExpectationsWithEdits)
  const categories = useSelector(selectMarketYoyGrowthExpectationCategories)
  const geoMarkets = useSelector(selectMarketYoyGrowthExpectationGeoMarkets)
  const expandedOfferingsList = useSelector(selectMarketYoyGrowthExpectationExpandedOfferings)

  const [isMarketsExpanded, setIsMarketsExpanded] = useState(false)
  const [selectedRtm, setSelectedRtm] = useState<TMarketYoyGrowthExpectationRtm>('Reseller')

  const hasDirtyEdits = useSelector(selectHasMarketYoyGrowthDirtyEdits)
  const isSaving = useSelector(selectIsMarketYoyGrowthSaving)
  const saveError = useSelector(selectMarketYoyGrowthSaveError)
  const workflowState = useSelector(selectMarketYoyGrowthExpectationsWorkflowState)
  const isPreviousFYearSelected = useSelector(selectIsMarketYoyGrowthPreviousFYearSelected)
  const selectedFYear = useSelector(selectMarketYoyGrowthSelectedFYear)

  const expandedOfferings = useMemo(() => new Set(expandedOfferingsList), [expandedOfferingsList])

  // Market users are locked to their own market; WorldWide users (and missing
  // userInfo, while the data source is mocked) pick the scope via the switcher.
  const isWorldWideView = userInfo.viewType === 'WorldWide'
  const isMarketView = userInfo.viewType === 'Market'
  const isAdminView = userInfo.viewType === 'Admin'

  const canPublish = workflowState === 'draft' && isWorldWideView && !isPreviousFYearSelected
  const canUnlock = workflowState === 'locked' && isAdminView

  const onToggleExpand = useCallback(
    (offering: ProgramTypes) => {
      dispatch(offeringExpandToggled(offering))
    },
    [dispatch]
  )

  const onToggleMarketsExpanded = useCallback(() => setIsMarketsExpanded((current) => !current), [])

  const onGrowthValueCommit = useCallback(
    (target: IMarketYoyGrowthEditTarget) => {
      dispatch(growthValueEdited(target))
    },
    [dispatch]
  )

  const onSave = useCallback(() => {
    dispatch(requestApplyYoyGrowthPlanningDetailsChanges())
  }, [dispatch])

  const onCancel = useCallback(() => {
    dispatch(growthEditsDiscarded())
  }, [dispatch])

  const onPublish = useCallback(() => {
    dispatch(requestApplyYoyGrowthPlanningChanges())
  }, [dispatch])

  const onUnlock = useCallback(() => {
    dispatch(requestApplyYoyGrowthPlanningChanges())
  }, [dispatch])

  const rtmPlanningDetails = useMemo(
    () => planningDetails.filter((record) => record.rtm === selectedRtm),
    [planningDetails, selectedRtm]
  )
  const rtmCategories = useMemo(
    () => categories.filter((category) => category.rtm === selectedRtm),
    [categories, selectedRtm]
  )

  const { groups, markets } = useMemo(
    () => getMarketYoyGrowthExpectationGroups(rtmPlanningDetails, rtmCategories, geoMarkets),
    [rtmPlanningDetails, rtmCategories, geoMarkets]
  )

  const rows = useMemo<TMarketYoyGrowthExpectationTableRow[]>(
    () =>
      groups.flatMap(({ group, items }) =>
        expandedOfferings.has(group.offering) ? [group, ...items] : [group]
      ),
    [groups, expandedOfferings]
  )

  const columns = useMemo(
    () =>
      getColumns({
        market: userInfo.market,
        isMarketView,
        markets,
        isMarketsExpanded,
        onToggleMarketsExpanded,
        expandedOfferings,
        onToggleExpand,
        isEditable,
        rtm: selectedRtm,
        onGrowthValueCommit,
      }),
    [
      userInfo.market,
      isMarketView,
      markets,
      isMarketsExpanded,
      onToggleMarketsExpanded,
      expandedOfferings,
      onToggleExpand,
      isEditable,
      selectedRtm,
      onGrowthValueCommit,
    ]
  )

  const rowTpl = useCallback<TplFunction<TMarketYoyGrowthExpectationTableRow>>(
    (item, renderChildren, rowState, rowAttrs, additional, focusedId) => {
      const { key, testId, onClick, className } = rowAttrs
      const rowClassName = [className, item.type === 'group' ? styles.groupRow : undefined]
        .filter(Boolean)
        .join(' ')

      return (
        <tr key={key} data-testid={testId} onClick={onClick} className={rowClassName}>
          {renderChildren(item, rowState, additional, focusedId, rowAttrs)}
        </tr>
      )
    },
    []
  )

  const onRowClick = useCallback(
    (item: TMarketYoyGrowthExpectationTableRow) => {
      if (item.type === 'group') {
        onToggleExpand(item.offering)
      }
    },
    [onToggleExpand]
  )

  return (
    <div className={styles.root}>
      <div className={styles.controlsTile}>
        <FYearSelect />

        <RtmSegmentedControl
          selectedRtm={selectedRtm}
          onSelect={setSelectedRtm}
          isDisabled={hasDirtyEdits || isSaving}
        />

        <div className={styles.controlsActions}>
          <ExportButton
            fYear={selectedFYear}
            rtm={selectedRtm}
            isDisabled={isSaving || !selectedFYear}
          />
          {(isEditable || canPublish || canUnlock) && (
            <>
              {saveError && <div className={styles.saveError}>{saveError}</div>}
              {isEditable && (
                <>
                  <Button
                    size="sm"
                    variant="text"
                    disabled={!hasDirtyEdits || isSaving}
                    onClick={onCancel}
                  >
                    <Translation text={KEYS.cancel} />
                  </Button>
                  <Button size="sm" disabled={!hasDirtyEdits || isSaving} onClick={onSave}>
                    <Translation text={KEYS.save} />
                  </Button>
                </>
              )}
              {canPublish && (
                <Button size="sm" disabled={isSaving} onClick={onPublish}>
                  <Translation text={KEYS.publish} />
                </Button>
              )}
              {canUnlock && (
                <Button size="sm" disabled={isSaving} onClick={onUnlock}>
                  <Translation text={KEYS.unlock} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <MarketYoyGrowthExpectationBanner />

      <div className={styles.tableTile}>
        <Table<TMarketYoyGrowthExpectationTableRow>
          header
          className={styles.table}
          items={rows}
          keyField="uid"
          columns={columns}
          rowTpl={rowTpl}
          onClick={onRowClick}
        />
      </div>
    </div>
  )
}

MarketYoyGrowthExpectationTable.displayName = 'MarketYoyGrowthExpectationTable'
